import React, { useState, useEffect, useMemo, useRef } from "react";
import ExportService from "../services/export.service";
import employeeService from "../services/employee.service";
import aiService from "../services/ai.service";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, Button, Typography, CircularProgress, Alert, Paper, Stack, useTheme, FormControl, InputLabel, Select, MenuItem, TextField, Divider, FormControlLabel, Checkbox } from '@mui/material';
import { format } from 'date-fns';

const CHAT_STORAGE_KEY = 'export_ai_chat_v1';
const MAX_CONTEXT_MESSAGES = 12;

const Export = () => {
    const theme = useTheme();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState('all');
    const [includeWorkplace, setIncludeWorkplace] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [chatError, setChatError] = useState('');
    const [showSql, setShowSql] = useState(false);
    const chatScrollRef = useRef(null);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await employeeService.getEmployees();
                setEmployees(response.data || []);
            } catch (err) {
                console.error("Error fetching employees", err);
            }
        };
        fetchEmployees();
    }, []);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(CHAT_STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) setChatMessages(parsed);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatMessages));
        } catch (e) {
            // ignore
        }
    }, [chatMessages]);

    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [chatMessages, chatLoading]);

    const chatContextMessages = useMemo(() => {
        const windowed = chatMessages.slice(-MAX_CONTEXT_MESSAGES);
        return windowed.map(({ role, content }) => ({ role, content }));
    }, [chatMessages]);

    const handleExport = async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        setError('');

        try {
            const employeeId = selectedEmployee === 'all' ? null : selectedEmployee;
            const response = await ExportService.exportAttendance(startDate, endDate, employeeId, includeWorkplace);

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = `attendance_${format(startDate, 'yyyy-MM-dd')}_-_${format(endDate, 'yyyy-MM-dd')}.xlsx`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch && filenameMatch.length > 1) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (err) {
            const resMessage =
                (err.response &&
                    err.response.data &&
                    err.response.data.message) ||
                err.message ||
                err.toString();
            setError(resMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSendChat = async () => {
        const text = (chatInput || '').trim();
        if (!text || chatLoading) return;

        setChatError('');
        setChatLoading(true);

        const nextMessages = [
            ...chatMessages,
            { role: 'user', content: text, ts: Date.now() },
        ];

        setChatMessages(nextMessages);
        setChatInput('');

        try {
            const response = await aiService.chat(
                [...chatContextMessages, { role: 'user', content: text }],
                'Europe/Bratislava'
            );

            const data = response?.data;
            if (data?.type === 'clarify') {
                const options = Array.isArray(data.options) && data.options.length
                    ? `\n\nMožnosti:\n- ${data.options.join('\n- ')}`
                    : '';

                setChatMessages((prev) => ([
                    ...prev,
                    { role: 'assistant', content: `${data.question || 'Vieš to upresniť?'}${options}`, ts: Date.now() }
                ]));
            } else if (data?.type === 'answer') {
                setChatMessages((prev) => ([
                    ...prev,
                    { role: 'assistant', content: data.answer || '(bez odpovede)', sql: data.sql || '', rowCount: data.rowCount, ts: Date.now() }
                ]));
            } else {
                setChatError('Neočakávaná odpoveď zo servera.');
            }
        } catch (err) {
            const resMessage =
                (err.response &&
                    err.response.data &&
                    err.response.data.message) ||
                err.message ||
                err.toString();
            setChatError(resMessage);
        } finally {
            setChatLoading(false);
        }
    };

    const handleClearChat = () => {
        setChatMessages([]);
        setChatError('');
        setChatInput('');
        try {
            localStorage.removeItem(CHAT_STORAGE_KEY);
        } catch (e) {
            // ignore
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 3, margin: 'auto', maxWidth: 1200, bgcolor: 'background.default' }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', sm: '1.75rem' }, mb: 0.5 }}>
                        Export Attendance
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 400, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                        Select a date range to export attendance data.
                    </Typography>
                </Box>
                <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, bgcolor: 'background.paper', boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <Typography variant="h6" gutterBottom>
                        Export Attendance
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center" sx={{ mt: 2 }}>
                        <DatePicker
                            label="From"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{
                                textField: { sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } },
                                day: { sx: { '&.Mui-selected': { backgroundColor: theme.palette.primary.main }, '&.Mui-selected:hover': { backgroundColor: theme.palette.primary.dark }, '&.Mui-selected:focus': { backgroundColor: theme.palette.primary.dark }, borderRadius: '8px' } },
                                desktopPaper: { sx: { borderRadius: '16px' } }
                            }}
                        />
                        <DatePicker
                            label="To"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{
                                textField: { sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } },
                                day: { sx: { '&.Mui-selected': { backgroundColor: theme.palette.primary.main }, '&.Mui-selected:hover': { backgroundColor: theme.palette.primary.dark }, '&.Mui-selected:focus': { backgroundColor: theme.palette.primary.dark }, borderRadius: '8px' } },
                                desktopPaper: { sx: { borderRadius: '16px' } }
                            }}
                        />
                        <FormControl sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                            <InputLabel>Zamestnanec</InputLabel>
                            <Select
                                value={selectedEmployee}
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                                label="Zamestnanec"
                            >
                                <MenuItem value="all">Všetci</MenuItem>
                                {employees.map((employee) => (
                                    <MenuItem key={employee.id} value={employee.id}>
                                        {employee.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={includeWorkplace}
                                    onChange={(e) => setIncludeWorkplace(e.target.checked)}
                                />
                            )}
                            label="Zobraziť pracovisko"
                            sx={{ m: 0, alignSelf: 'center' }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleExport}
                            disabled={!startDate || !endDate || loading}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                py: 1.25,
                                px: 3,
                                alignSelf: 'stretch',
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Export'}
                        </Button>
                    </Stack>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                <Paper
                    sx={{
                        mt: 3,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                            <Typography variant="h6">AI Chat (MVP)</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Pýtaj sa na dochádzku a štatistiky. História sa ukladá lokálne (window posledných správ).
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1.25}>
                            <Button
                                variant={showSql ? "contained" : "outlined"}
                                onClick={() => setShowSql((v) => !v)}
                                disabled={chatLoading}
                                sx={{ borderRadius: '8px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                            >
                                {showSql ? 'Skryť SQL' : 'Zobraziť SQL'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClearChat}
                                disabled={chatLoading || chatMessages.length === 0}
                                sx={{ borderRadius: '8px' }}
                            >
                                Vymazať históriu
                            </Button>
                        </Stack>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        ref={chatScrollRef}
                        sx={{
                            height: 320,
                            overflowY: 'auto',
                            p: 1,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
                        }}
                    >
                        {chatMessages.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', p: 1 }}>
                                Skús napríklad: „Kedy prišiel Viktor Šuster do práce v pondelok minulý týždeň?“
                            </Typography>
                        ) : (
                            <Stack spacing={1.25} sx={{ p: 1 }}>
                                {chatMessages.map((m, idx) => {
                                    const isUser = m.role === 'user';
                                    return (
                                        <Box
                                            key={`${m.ts || 't'}-${idx}`}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: isUser ? 'flex-end' : 'flex-start'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    maxWidth: '85%',
                                                    px: 1.5,
                                                    py: 1,
                                                    borderRadius: 2,
                                                    bgcolor: isUser
                                                        ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
                                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                    {m.content}
                                                </Typography>
                                                {!isUser && showSql && m.sql && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            SQL{typeof m.rowCount === 'number' ? ` (rows: ${m.rowCount})` : ''}:
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                mt: 0.5,
                                                                p: 1,
                                                                borderRadius: 1,
                                                                border: `1px solid ${theme.palette.divider}`,
                                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                                fontSize: '0.75rem',
                                                                lineHeight: 1.4,
                                                                color: 'text.primary',
                                                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.7)',
                                                                whiteSpace: 'pre-wrap'
                                                            }}
                                                        >
                                                            {m.sql}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {chatLoading && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        AI premýšľa...
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Box>

                    {chatError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {chatError}
                        </Alert>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Napíš otázku..."
                            disabled={chatLoading}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendChat();
                                }
                            }}
                            multiline
                            minRows={1}
                            maxRows={4}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSendChat}
                            disabled={chatLoading || !chatInput.trim()}
                            sx={{
                                borderRadius: '8px',
                                boxShadow: 'none',
                                px: 3,
                                '&:hover': { boxShadow: 'none' }
                            }}
                        >
                            {chatLoading ? <CircularProgress size={22} color="inherit" /> : 'Odoslať'}
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        </LocalizationProvider>
    );
};

export default Export; 